import {
  BlockNoteEditor,
  BlockSchema,
  InlineContentSchema,
  insertOrUpdateBlock,
  StyleSchema,
} from '@blocknote/core';
import {
  createReactBlockSpec,
  DefaultReactSuggestionItem,
  ReactCustomBlockRenderProps,
} from '@blocknote/react';
import { getEditorMode } from 'utils/createEditorWithMode';
import { ReactElement } from 'react';
import { TextQuestionIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import { useBlockDelete } from 'hooks/useBlockDelete';
import { TeacherTQBlock } from './teacher/TeacherTQBlock';
import { StudentTQBlock } from './student/StudentTQBlock';
import { ReviewTQBlock } from './reviewer/ReviewTQBlock';
import { BlockBorderBox } from '../common/BlockBorderBox';

export type TQBlockProps = ReactCustomBlockRenderProps<
  (typeof TextQuestionBlock)['config'],
  InlineContentSchema,
  StyleSchema
>;

export type TQBlockUpdate = {
  question?: string;
  answer?: string;
  correctAnswer?: string;
  isSubmitted?: boolean;
};

const propSchema = {
  question: {
    default: '',
    type: 'string',
    description: 'The question text',
  },
  answer: {
    default: '',
    type: 'string',
    description: "Student's answer",
  },
  correctAnswer: {
    default: '',
    type: 'string',
    description: 'The correct answer',
  },
  isSubmitted: {
    default: false,
    type: 'boolean',
    description: 'Whether the answer has been submitted',
  },
};

const TextQuestionBlockContent = ({ props }: { props: TQBlockProps }): ReactElement => {
  const { editor, block } = props;
  const mode = getEditorMode(editor);

  const { isDeleting, handleDelete } = useBlockDelete({
    editor,
    block,
    name: 'Textfrage',
  });

  const handleBlockUpdate = (updates: TQBlockUpdate): void => {
    editor.updateBlock(block, {
      props: {
        ...block.props,
        ...updates,
      },
    });
  };

  const handleSeparatorClick = (): void => {
    editor.focus();
    editor.setTextCursorPosition(block, 'end');
    // Simulate Enter key press to create a new block
    const enterEvent = new KeyboardEvent('keydown', {
      key: 'Enter',
      code: 'Enter',
      keyCode: 13,
      which: 13,
      bubbles: true,
      cancelable: true,
      composed: true,
    });
    editor.domElement?.dispatchEvent(enterEvent);
    editor.openSuggestionMenu('/');
  };

  const getBlockByMode = (): ReactElement | null => {
    switch (mode) {
      case 'author':
        return (
          <TeacherTQBlock {...props} onBlockUpdate={handleBlockUpdate} onDelete={handleDelete} />
        );
      case 'student':
        return <StudentTQBlock {...props} onBlockUpdate={handleBlockUpdate} />;
      case 'review':
        return <ReviewTQBlock {...props} />;
      default:
        return null;
    }
  };

  return (
    <BlockBorderBox
      isDeleting={isDeleting}
      onSeparatorClick={mode === 'author' ? handleSeparatorClick : undefined}
      shouldShowSeparator={mode === 'author'}
      marginTop={{ base: '15px', md: '20px' }}
      marginBottom={{ base: '10px', md: '15px' }}
    >
      {getBlockByMode()}
    </BlockBorderBox>
  );
};

const TextQuestionBlock = createReactBlockSpec(
  {
    type: 'textQuestion',
    propSchema,
    content: 'inline',
  },
  {
    render: (props) => <TextQuestionBlockContent props={props} />,
  }
);

const createTextQuestionSlashItem = <T extends BlockSchema>(
  editor: BlockNoteEditor<T>
): DefaultReactSuggestionItem => ({
  title: 'Textfrage',
  onItemClick: () => {
    const insertedBlock = insertOrUpdateBlock(editor, {
      type: 'textQuestion',
    });

    // Add a paragraph block after the inserted block
    editor.insertBlocks(['paragraph'], insertedBlock, 'after');

    // Focus the new block
    editor.focus();
    editor.setTextCursorPosition(insertedBlock, 'start');
  },
  aliases: ['textQuestion', 'tq', 'text', 'question'],
  group: 'Interaktiv',
  icon: <NovaIcon icon={TextQuestionIcon} size="m" />,
  subtext: 'Frage mit Freitextantwort',
});

export { TextQuestionBlock, createTextQuestionSlashItem };
